import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import { propertyData } from '../../mocks/property';

interface ExposePDFProps {
  solidNav?: boolean;
}

export default function ExposePDF({ solidNav = true }: ExposePDFProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const gold = [181, 154, 106] as [number, number, number];
      const dark = [28, 28, 28] as [number, number, number];
      const mid = [107, 100, 96] as [number, number, number];
      const light = [245, 243, 239] as [number, number, number];

      // Background
      doc.setFillColor(...light);
      doc.rect(0, 0, 210, 297, 'F');

      // Top gold bar
      doc.setFillColor(...gold);
      doc.rect(0, 0, 210, 3, 'F');

      // Sidebar
      doc.setFillColor(240, 237, 232);
      doc.rect(0, 0, 52, 297, 'F');

      // White main area
      doc.setFillColor(252, 252, 249);
      doc.rect(60, 20, 140, 257, 'F');

      // ─── Sidebar content ───
      doc.setTextColor(...gold);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('THE OBSIDIAN VILLA', 10, 30, { angle: 0 });

      doc.setDrawColor(...gold);
      doc.setLineWidth(0.4);
      doc.line(10, 33, 42, 33);

      doc.setTextColor(...mid);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text('EXKLUSIVES EXPOSÉ', 10, 39);
      doc.text('Zürichberg, Zürich', 10, 45);
      doc.text('Schweiz', 10, 50);

      // Price block
      doc.setFillColor(...gold);
      doc.rect(10, 60, 32, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      doc.text('KAUFPREIS', 14, 67);
      doc.setFontSize(8);
      doc.text('CHF 5.8 Mio.', 14, 73);

      // Highlights on sidebar
      doc.setTextColor(...gold);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text('KENNZAHLEN', 10, 90);
      doc.setDrawColor(...gold);
      doc.line(10, 92, 42, 92);

      const sideData = [
        ['Wohnfläche', '680 m²'],
        ['Grundstück', '2.400 m²'],
        ['Schlafzimmer', '6'],
        ['Badezimmer', '7'],
        ['Baujahr', '2021'],
        ['Garage', '4 Fahrzeuge'],
        ['Pool', 'Infinity'],
        ['Energie', 'AAA'],
      ];

      sideData.forEach(([label, value], i) => {
        const y = 99 + i * 13;
        doc.setTextColor(...mid);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.text(label, 10, y);
        doc.setTextColor(...dark);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.text(value, 10, y + 5);
        doc.setDrawColor(220, 215, 210);
        doc.setLineWidth(0.2);
        doc.line(10, y + 8, 44, y + 8);
      });

      // Agent on sidebar
      doc.setTextColor(...gold);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text('KONTAKT', 10, 215);
      doc.setDrawColor(...gold);
      doc.line(10, 217, 42, 217);

      doc.setTextColor(...dark);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(propertyData.agent.name, 10, 224);
      doc.setTextColor(...mid);
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.text(propertyData.agent.title, 10, 229);
      doc.setTextColor(...dark);
      doc.text(propertyData.agent.phone, 10, 236);
      doc.text(propertyData.agent.email, 10, 241, { maxWidth: 44 });
      doc.text('Bahnhofstrasse 12', 10, 248);
      doc.text('8001 Zürich', 10, 253);

      // ─── Main content ───
      // Header
      doc.setTextColor(...gold);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('THE OBSIDIAN VILLA', 68, 35);

      doc.setTextColor(...dark);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Obsidian Villa', 68, 50);

      doc.setTextColor(...gold);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Zürichberg · Zürich · Schweiz', 68, 58);

      doc.setDrawColor(...gold);
      doc.setLineWidth(0.5);
      doc.line(68, 63, 190, 63);

      // Description
      doc.setTextColor(...gold);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('ÜBER DIE IMMOBILIE', 68, 73);

      doc.setTextColor(80, 74, 70);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(propertyData.description, 115);
      doc.text(descLines, 68, 80);

      // Materials section
      const matY = 80 + descLines.length * 4.5 + 8;
      doc.setTextColor(...gold);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('AUSSTATTUNG & MATERIALIEN', 68, matY);
      doc.setDrawColor(220, 215, 210);
      doc.setLineWidth(0.2);
      doc.line(68, matY + 3, 190, matY + 3);

      const matItems = [
        ['Bodenbelag', 'Calacatta Marmor & Eichenparkett'],
        ['Küche', 'Bulthaup · Brass & Oak Bespoke'],
        ['Badezimmer', 'Gessi Armaturenreihe'],
        ['Haustechnik', 'Loxone Hausautomation'],
        ['Verglasung', 'Schüco Dreifach Wärmeschutz'],
        ['Sicherheit', '24/7 Smart Security System'],
      ];

      matItems.forEach(([label, value], i) => {
        const y = matY + 10 + i * 11;
        doc.setTextColor(...mid);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.text(label.toUpperCase(), 68, y);
        doc.setTextColor(...dark);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 68, y + 5);
        doc.setDrawColor(230, 226, 221);
        doc.setLineWidth(0.15);
        doc.line(68, y + 7.5, 190, y + 7.5);
      });

      // Discretion note at bottom
      doc.setFillColor(...gold);
      doc.rect(60, 270, 140, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text(
        'Dieses Exposé ist vertraulich und ausschließlich für den adressierten Empfänger bestimmt.',
        130,
        274.5,
        { align: 'center' }
      );

      // Bottom gold bar
      doc.setFillColor(...gold);
      doc.rect(0, 294, 210, 3, 'F');

      doc.save('The-Obsidian-Villa-Expose.pdf');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={generate}
      disabled={loading}
      className={`flex items-center gap-1.5 font-sans text-[10px] tracking-widest uppercase transition-colors duration-300 cursor-pointer disabled:opacity-50 whitespace-nowrap ${
        solidNav
          ? 'text-[#6B6460] hover:text-[#B59A6A]'
          : 'text-white/70 hover:text-[#B59A6A]'
      }`}
    >
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-download-line text-xs" />
      </div>
      {loading ? t('pdf.downloading') : t('pdf.download')}
    </button>
  );
}
